import React, { useState } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ImportSalesforceList from './ImportSalesforceList';
import type { SalesforceListImportType } from './ImportSalesforceList';
import { EnumProspectType } from '../../../types/prospects';
import { secondaryActionButtonSx } from '../../modal/ModalActionButtons';

interface Props {
  onConfirm: (rows: any[]) => void;
  onCancel: () => void;
  prospectType: EnumProspectType;
  hideCancel?: boolean;
}

type ImportTypeState = SalesforceListImportType | null;

interface SourceOption {
  id: SalesforceListImportType;
  label: string;
  caption: string;
  icon: React.ReactNode;
}

const ImportSalesforce: React.FC<Props> = ({
  onConfirm,
  onCancel,
  prospectType,
  hideCancel = false
}) => {
  const [importType, setImportType] = useState<ImportTypeState>(null);

  const handleBack = () => {
    if (importType) {
      setImportType(null);
    } else {
      onCancel();
    }
  };

  const showAccountImport = prospectType === EnumProspectType.ACCOUNT;
  const showLeadImport = prospectType === EnumProspectType.LEAD;

  const sourceOptions: SourceOption[] = [
    ...(showAccountImport
      ? [
          {
            id: 'ACCOUNTS' as const,
            label: 'Accounts',
            caption: 'Import from Salesforce Accounts',
            icon: <BusinessIcon sx={{ fontSize: 24 }} />
          }
        ]
      : []),
    ...(showLeadImport
      ? [
          {
            id: 'CONTACTS' as const,
            label: 'Contacts',
            caption: 'Import from Salesforce Contacts',
            icon: <PersonIcon sx={{ fontSize: 24 }} />
          }
        ]
      : [])
  ];

  if (!importType) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.secondary">
          Choose where to import from
        </Typography>
        <Grid container spacing={1.5}>
          {sourceOptions.map((opt) => (
            <Grid item xs={6} key={opt.id}>
              <Box
                component="button"
                onClick={() => setImportType(opt.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%',
                  minHeight: 100,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: 'background.paper',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: (theme) =>
                      theme.customColors?.themePrimary ?? 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    color: (theme) =>
                      theme.customColors?.themePrimary ?? 'primary.main'
                  }}
                >
                  {opt.icon}
                </Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {opt.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2 }}
                >
                  {opt.caption}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {!hideCancel && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 0.5 }}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={secondaryActionButtonSx}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <ImportSalesforceList
      importType={importType}
      onConfirm={onConfirm}
      onCancel={handleBack}
      hideCancel={hideCancel}
    />
  );
};

export default ImportSalesforce;
